import supabase from "@/lib/supabase";
import { create } from "zustand";
import { persist, createJSONStorage } from 'zustand/middleware'

import useAuthStore from "./useAuthStore";
import useAudioStore from "./useAudioStore";
import { useMessageStore } from "./useMessageStore";
import STORAGE_KEYS from '@/constant/storage';

interface ConfigStore {
  deviceType: string;
  learnTutorial: boolean;
  gameTutorial: boolean;
  filterSubject: {
    value: string;
    label: string;
  };
  filterGrade: {
    value: string;
    label: string;
  };
  filterSchool: {
    value: string;
    label: string;
  };
  betterExperienceTip: boolean;
  feedbackInput: boolean;
  finishLearnTutorial: () => void;
  finishGameTutorial: () => void;
  checkShowBetterExperienceTip: () => void;
  closeBetterExperienceTip: () => void;
}

// Add zustand middleware, cache filterSubject and filterGrade, using persister

const useConfigStore = create<ConfigStore>()(persist((set, get) => ({
  deviceType: '',
  learnTutorial: false,
  gameTutorial: false,
  betterExperienceTip: false,
  filterSubject: {
    value: 'all',
    label: 'All Subjects',
  },
  filterGrade: {
    value: 'all',
    label: 'All Grades',
  },
  filterSchool: {
    value: 'all',
    label: 'All Schools',
  },
  feedbackInput: false,
  finishLearnTutorial: async () => {
    const user = useAuthStore.getState().user;
    set({ learnTutorial: false });

    // 标记 tutorial 已完成到 localStorage，防止重新出现
    localStorage.setItem(STORAGE_KEYS.LEARN_TUTORIAL, 'false');

    const welcome = useMessageStore.getState().messages[0];
    if (welcome.role === 'assistant' && welcome.type === 'text') {
      useAudioStore.setState({ playingId: welcome.id });
      useAudioStore.setState({ playingType: 'assistant' });
      get().checkShowBetterExperienceTip();
      await useAudioStore.getState().playTTS(welcome.content || '');
    }

    await supabase
    .from('profiles')
    .update({ learn_tutorial: false })
    .eq('id', user?.id)
  },
  finishGameTutorial: async () => {
    const user = useAuthStore.getState().user;
    set({ gameTutorial: false });

    // 标记 tutorial 已完成到 localStorage，防止重新出现
    localStorage.setItem(STORAGE_KEYS.GAME_TUTORIAL, 'false');

    await supabase
    .from('profiles')
    .update({ game_tutorial: false })
    .eq('id', user?.id)
  },
  checkShowBetterExperienceTip: () => {
    const deviceType = get().deviceType;
    const betterExperienceTipData = localStorage.getItem(STORAGE_KEYS.BETTER_EXPERIENCE_TIP);

    // 检查是否是今天关闭的
    let shouldShow = true;
    if (betterExperienceTipData) {
      try {
        const savedDate = betterExperienceTipData;
        const today = new Date().toDateString();
        // 如果保存的日期是今天，说明今天已经关闭过，不再显示
        if (savedDate === today) {
          shouldShow = false;
        }
      } catch (e) {
        // 如果数据格式不对（比如旧的 'false' 值），清除并重新开始
        localStorage.removeItem(STORAGE_KEYS.BETTER_EXPERIENCE_TIP);
      }
    }

    if (deviceType === 'mobile' && shouldShow) {
      // Get current URL
      const currentUrl = window.location.href;
      if (currentUrl.includes('/learn')) {
        if (!get().learnTutorial) {
          set({ betterExperienceTip: true });
        }
      }
      if (currentUrl.includes('/practice')) {
        set({ betterExperienceTip: true });
      }
      if (currentUrl.includes('/play')) {
        set({ betterExperienceTip: true });
      }
      if (currentUrl.includes('/quest')) {
        set({ betterExperienceTip: true });
      }
    }
  },
  closeBetterExperienceTip: () => {
    // User actively closes tip - save today's date
    const today = new Date().toDateString();
    localStorage.setItem(STORAGE_KEYS.BETTER_EXPERIENCE_TIP, today);
    set({ betterExperienceTip: false });
  }
}), {
  partialize: (state) => ({ filterSubject: state.filterSubject, filterGrade: state.filterGrade, filterSchool: state.filterSchool }),
  name: 'config-filter-options',
  storage: createJSONStorage(() => localStorage),
}));

export default useConfigStore;